import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Download, Share2, Twitter, Facebook, MessageCircle } from "lucide-react";

export default function ShareDialog({ open, onOpenChange, question }) {
  const [copied, setCopied] = useState(false);
  if (!question) return null;

  const shareUrl = `${API}/share/${question.id}`;
  const cardUrl = `${API}/questions/${question.id}/card.png`;
  const text = `\u201c${question.question_kjv}\u201d \u2014 ${question.reference}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "QuestionGod.org", text, url: shareUrl });
      } catch (e) {
        if (e?.name !== "AbortError") console.warn("Native share failed", e);
      }
    } else {
      copy();
    }
  };

  const targets = [
    { key: "x", label: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}` },
    { key: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md overflow-hidden sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Share this question</DialogTitle>
          <DialogDescription>Send a friend into the study with a ready-made preview card.</DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden rounded-xl border border-border">
          <img
            src={cardUrl}
            alt={text}
            data-testid="share-card-preview"
            className="aspect-[1200/630] w-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="flex min-w-0 items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1.5">
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground" data-testid="share-url">{shareUrl}</span>
          <button
            data-testid="share-copy-button"
            onClick={copy}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            aria-label="Copy link"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {targets.map((t) => (
            <a
              key={t.key}
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-${t.key}`}
              className="flex items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-medium transition-colors hover:border-gold hover:text-primary"
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </a>
          ))}
        </div>

        <div className="flex gap-2">
          <Button data-testid="share-native-button" onClick={nativeShare} className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Share2 className="mr-2 h-4 w-4" /> Share
          </Button>
          <Button asChild variant="outline" className="rounded-full">
            <a href={cardUrl} download={`questiongod-${question.id}.png`} data-testid="share-download-button">
              <Download className="mr-2 h-4 w-4" /> Image
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
